import { useEffect, type ReactNode } from "react";
import { createPortal } from "react-dom";
import { normalizarTextoPtBr } from "../../utils/textoPtBr";
import { useIdioma } from "../../i18n/IdiomaProvider";
import { BotaoAcao, BotaoContorno } from "./BotaoAcao";
import { BotaoTopoPrimario } from "./BotoesTopo";
import { CabecalhoModal, RodapeModal } from "./CascaModal";
import styles from "./ModalConfirmacao.module.css";

interface ModalConfirmacaoProps {
  aberto: boolean;
  titulo?: string;
  mensagem: ReactNode;
  textoConfirmar?: string;
  textoCancelar?: string;
  perigo?: boolean;
  processando?: boolean;
  onConfirmar: () => void;
  onCancelar: () => void;
}

const IconeAlerta = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M10.3 3.9 1.8 18a2 2 0 0 0 1.7 3h17a2 2 0 0 0 1.7-3L13.7 3.9a2 2 0 0 0-3.4 0Z" />
    <path d="M12 9v4M12 17h.01" />
  </svg>
);

/** Modal de confirmação compacto e centralizado (padrão do legado). */
export function ModalConfirmacao({
  aberto,
  titulo = "Confirmar",
  mensagem,
  textoConfirmar = "Confirmar",
  textoCancelar = "Cancelar",
  perigo = false,
  processando = false,
  onConfirmar,
  onCancelar,
}: ModalConfirmacaoProps) {
  const { traduzir } = useIdioma();
  const tituloNormalizado = traduzir(normalizarTextoPtBr(titulo));
  const textoConfirmarNormalizado = traduzir(normalizarTextoPtBr(textoConfirmar));
  const textoCancelarNormalizado = traduzir(normalizarTextoPtBr(textoCancelar));
  const confirmarPerigoso =
    perigo || /excluir/i.test(`${titulo} ${textoConfirmar}`);
  const mensagemNormalizada =
    typeof mensagem === "string" ? traduzir(normalizarTextoPtBr(mensagem)) : mensagem;

  useEffect(() => {
    if (!aberto) return;
    const aoTecla = (e: KeyboardEvent) => {
      if (e.key === "Escape" && !processando) onCancelar();
    };
    window.addEventListener("keydown", aoTecla);
    return () => window.removeEventListener("keydown", aoTecla);
  }, [aberto, processando, onCancelar]);

  if (!aberto) return null;

  return createPortal(
    <div
      data-modal-overlay="true"
      className={styles.overlay}
      onClick={() => !processando && onCancelar()}
      role="dialog"
      aria-modal="true"
    >
      <div data-vidro="true" data-modal-surface="true" className={styles.card} onClick={(e) => e.stopPropagation()}>
        <CabecalhoModal
          icone={<IconeAlerta />}
          titulo={tituloNormalizado}
          onFechar={onCancelar}
          className={confirmarPerigoso ? styles.cabecalhoPerigo : undefined}
        />

        <div className={styles.mensagem}>{mensagemNormalizada}</div>

        <RodapeModal>
          <BotaoContorno
            className={styles.botaoRodape}
            estilo="topo"
            onClick={onCancelar}
            disabled={processando}
          >
            {textoCancelarNormalizado}
          </BotaoContorno>
          {confirmarPerigoso ? (
            <BotaoAcao
              className={styles.botaoRodape}
              estilo="topo"
              variante="perigo"
              carregando={processando}
              onClick={onConfirmar}
            >
              {textoConfirmarNormalizado}
            </BotaoAcao>
          ) : (
            <BotaoTopoPrimario
              className={styles.botaoRodape}
              carregando={processando}
              onClick={onConfirmar}
            >
              {textoConfirmarNormalizado}
            </BotaoTopoPrimario>
          )}
        </RodapeModal>
      </div>
    </div>,
    document.body
  );
}
